import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Button, ButtonSize } from './Button';
import { Input } from './Field';
import { IconCheck, IconCopy } from './icons';

/** How long "Copied" stays before the button offers to copy again. */
const COPIED_MS = 2000;

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    /* fall through to the legacy path */
  }
  if (typeof document.execCommand !== 'function') return false;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  try {
    area.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}

/**
 * CopyButton: puts `text` on the clipboard and says so in place — the label
 * flips to "Copied" for a moment (no toast). If the clipboard is blocked it
 * stays "Copy"; keep the value visible nearby (see `CopyField`) so it can
 * still be selected by hand.
 */
export const CopyButton: React.FC<{
  text: string;
  /** The resting label. */
  children?: React.ReactNode;
  copiedLabel?: string;
  variant?: 'secondary' | 'ghost';
  size?: ButtonSize;
  className?: string;
  'aria-label'?: string;
}> = ({
  text,
  children = 'Copy',
  copiedLabel = 'Copied',
  variant = 'secondary',
  size = 'md',
  className,
  'aria-label': ariaLabel,
}) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <Button
      variant={variant}
      size={size}
      aria-label={ariaLabel}
      leading={
        copied ? (
          <IconCheck size={14} className="shrink-0" />
        ) : (
          <IconCopy size={14} className="shrink-0" />
        )
      }
      onClick={async () => {
        if (!(await writeClipboard(text))) return;
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), COPIED_MS);
      }}
      className={clsx(
        copied &&
          'border-v2-success/40 bg-v2-success/10 text-v2-success hover:bg-v2-success/10 hover:text-v2-success',
        className,
      )}
    >
      <span role="status">{copied ? copiedLabel : children}</span>
    </Button>
  );
};

/**
 * CopyField: a value that exists to be passed on — an intake link, an API
 * key. A read-only field (click selects the whole value) with a CopyButton
 * beside it. Label it like any field, or pass `aria-label`.
 */
export const CopyField: React.FC<{
  value: string;
  id?: string;
  className?: string;
  'aria-label'?: string;
}> = ({ value, id, className, 'aria-label': ariaLabel }) => (
  <div className={clsx('flex items-center gap-2', className)}>
    <Input
      id={id}
      readOnly
      value={value}
      aria-label={ariaLabel}
      onFocus={(e) => e.currentTarget.select()}
      // The click's mouseup would collapse onFocus's selection to a caret;
      // re-select unless the user dragged a selection of their own.
      onClick={(e) => {
        const el = e.currentTarget;
        if (el.selectionStart === el.selectionEnd) el.select();
      }}
      className="v2-num min-w-0 flex-1 bg-v2-sunken text-v2-fg-2"
    />
    <CopyButton text={value} className="shrink-0" />
  </div>
);
